import StencilHTMLElement from "./stencil-element";

/**
 * Parent element that manages state for all nested product choices
 * 
 * Tracks selections for each nested choice:
 * - Which nested product is selected
 * - Which variant options are selected for that product
 * - Resolves to final variant IDs for cart addition
 * 
 * Usage:
 * <product-set-picker>
 *   <product-set-component data-index="1">...</product-set-component>
 *   <product-set-component data-index="2">...</product-set-component>
 * </product-set-picker>
 */
class ProductSetPicker extends StencilHTMLElement {
  name = "ProductSetPicker";
  
  /**
   * State structure:
   * {
   *   [choiceIndex]: {
   *     productId: string,
   *     productHandle: string,
   *     id: number | null
   *     price: number | null
   *     compareAtPrice: number | null
   *     available: boolean | null
   *     image: { src: string } | null
   *     shippingFrom: string | null
   *     ignorePriceEnabled: boolean
   *   }
   * }
   */
  state = {};

  /**
   * Cache for resolved variants
   * Key format: "{productHandle}:{sortedOptionValueIds}"
   * Value: { id: number | null, price: number | null, compareAtPrice: number | null, available: boolean | null, image: { src: string } | null, shippingFrom: string | null }
   */
  variantCache = {};

  nestedChoices;
  addToCartButton;

  constructor() {
    super();
  }

  connectedCallback() {
    // Find all child product-set-component elements
    this.nestedChoices = this.querySelectorAll('product-set-component');
    
    // Initialize state for each nested choice
    this.nestedChoices.forEach(choice => {
      const choiceIndex = choice.dataset.index;
      if (choiceIndex) {
        this.initializeChoiceState(choiceIndex, choice);
      }
    });

    // Listen for product changes from child elements
    this.addEventListener('product-set-component:product-changed', this.handleProductChange.bind(this));
    
    // Listen for product option changes from child elements
    this.addEventListener('product-set-component:product-option-changed', this.handleProductOptionChange.bind(this));

    // Listen for add to cart button clicks
    this.addToCartButton = this.querySelector('button[data-psp-add-to-cart]');
    this.addToCartButton.addEventListener('click', this.handleAddToCartClick.bind(this));

  }

  disconnectedCallback() {
    this.removeEventListener('product-set-component:product-changed', this.handleProductChange);
    this.removeEventListener('product-set-component:product-option-changed', this.handleProductOptionChange);
    
    this.addToCartButton.removeEventListener('click', this.handleAddToCartClick);
  }

  /**
   * Get the choice element by its index
   * @param {string} choiceIndex 
   * @returns {HTMLElement|null}
   */
  getChoiceElement(choiceIndex) {
    return this.querySelector(`product-set-component[data-index="${choiceIndex}"]`);
  }

  /**
   * Initialize state for a nested choice
   * @param {string} choiceIndex 
   * @param {HTMLElement} choiceElement 
   */
  async initializeChoiceState(choiceIndex, choiceElement) {
    const checkedRadio = choiceElement.querySelector('[data-psc-product-id]:checked');
    
    if (!checkedRadio) {
      console.log(this.logPrefix, 'Tried to initialise choice state, but no radio button was checked by default');
      this.state[choiceIndex] = {
        productId: null,
        productHandle: null,
        id: null
      };
      return;
    }

    const productId = checkedRadio.value;
    const productHandle = checkedRadio.dataset.pscProductHandle;
    
    this.state[choiceIndex] = {
      productId,
      productHandle,
      id: null, // Will be resolved when variant options are selected
      price: null,
      compareAtPrice: null,
      available: null,
      image: null,
      shippingFrom: null,
      ignorePriceEnabled: this.isIgnorePriceEnabled(choiceElement)
    };

    // Try to resolve variant immediately if options are already selected
    // Get option value IDs from the child element
    const optionValueIds = choiceElement.getSelectedOptionValueIds();
    await this.resolveVariantForChoice(choiceIndex, optionValueIds);
  }

  /**
   * Handle when a nested product changes (radio button)
   * @param {CustomEvent} e 
   */
  async handleProductChange(e) {
    const { choiceIndex, productId, productHandle } = e.detail;
    const choiceElement = this.getChoiceElement(choiceIndex);
    
    if (!choiceElement) return;

    if (!this.state[choiceIndex]) {
      this.state[choiceIndex] = {};
    }
    
    this.state[choiceIndex].productId = productId;
    this.state[choiceIndex].productHandle = productHandle;
    this.resetResolvedVariantState(this.state[choiceIndex]);
    this.state[choiceIndex].ignorePriceEnabled = this.isIgnorePriceEnabled(choiceElement);
    
    // Get option value IDs from the child element and resolve variant
    const optionValueIds = choiceElement.getSelectedOptionValueIds();
    await this.resolveVariantForChoice(choiceIndex, optionValueIds);
    
    // Dispatch event that state has changed
    this.dispatchStateChange();
  }

  /**
   * Handle when product options change within a nested choice
   * @param {CustomEvent} e 
   */
  async handleProductOptionChange(e) {
    const { choiceIndex, optionValueIds } = e.detail;
    
    if (!this.state[choiceIndex]?.productId) return;
    
    // Resolve variant using the option value IDs from the child element
    await this.resolveVariantForChoice(choiceIndex, optionValueIds);
    
    // Dispatch event that state has changed
    this.dispatchStateChange();
  }

  /**
   * Generate cache key from product handle and option value IDs
   * @param {string} productHandle 
   * @param {Array<number>} optionValueIds 
   * @returns {string}
   */
  getCacheKey(productHandle, optionValueIds) {
    // Sort option value IDs to ensure consistent cache keys
    const sortedIds = [...optionValueIds].sort((a, b) => a - b).join(',');
    return `${productHandle}:${sortedIds}`;
  }

  /**
   * Resolve the variant ID for a choice based on selected product and options
   * Uses Shopify API: GET /products/{handle}?option_values={ids}&view=json
   * Caches results for faster lookups when switching between options
   * @param {string} choiceIndex 
   * @param {Array<number>} optionValueIds - Array of selected option value IDs
   */
  async resolveVariantForChoice(choiceIndex, optionValueIds) {
    const choiceState = this.state[choiceIndex];
    if (!choiceState?.productId || !choiceState.productHandle) {
      console.log(this.logPrefix, 'Tried to resolve variant for choice, but no product ID or product handle was found');
      this.resetResolvedVariantState(choiceState);
      return;
    }
    
    // Need at least one option value to resolve variant
    if (!optionValueIds?.length) {
      console.log(this.logPrefix, 'Tried to resolve variant for choice, but no option value IDs were found');
      this.resetResolvedVariantState(choiceState);
      return;
    }

    // Check cache first
    const cacheKey = this.getCacheKey(choiceState.productHandle, optionValueIds);
    const cachedVariant = this.variantCache[cacheKey];
    if (cachedVariant !== undefined) {
      console.log(this.logPrefix, '🎯 Cache hit! Found cached variant for choice', cacheKey);
      this.applyResolvedVariant(choiceState, cachedVariant);
      this.updateChoiceImage(choiceIndex);
      return;
    }

    console.log(this.logPrefix, '🧨 Cache miss! Fetching variant for choice', cacheKey);

    try {
      // Build API URL: /products/{handle}?option_values={id1},{id2},...&view=json
      const optionValuesParam = optionValueIds.join(',');
      const apiUrl = `${window.Shopify.routes.root}products/${choiceState.productHandle}?option_values=${optionValuesParam}&view=json`;
      
      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch variant: ${response.status}`);
      }
      
      const data = await response.json();
      const variant = data?.variant;
      // The JSON template returns the selected_or_first_available_variant under `variant`
      if (variant?.id) {
        const resolvedVariant = this.buildResolvedVariant(variant, data);
        this.variantCache[cacheKey] = resolvedVariant;
        this.applyResolvedVariant(choiceState, resolvedVariant);
        this.updateChoiceImage(choiceIndex);
        return;
      }

      console.log(this.logPrefix, '🚨 No variant found for choice', cacheKey);
      
      this.resetResolvedVariantState(choiceState);
      this.variantCache[cacheKey] = this.emptyVariantCacheEntry();
    } catch (error) {
      console.error(this.logPrefix, "Failed to resolve variant", error);
      this.resetResolvedVariantState(choiceState);
      this.variantCache[cacheKey] = this.emptyVariantCacheEntry();
    }
  }

  /**
   * Dispatch a custom event when state changes
   */
  dispatchStateChange() {
    this.dispatchEvent(new CustomEvent('product-set-picker:state-changed', {
      detail: {
        state: { ...this.state },
        resolvedVariants: this.getResolvedVariants()
      },
      bubbles: true
    }));

    this.updateCompareAtPriceDisplay();
    this.updatePriceDisplay();
    this.updateAvailabilityDisplay();
    this.updateEstimatedDeliveryDisplay();
  }

  /**
   * Update the compare-at price display based on resolved variants
   */
  updateCompareAtPriceDisplay() {
    const compareAtTotal = this.getCompareAtTotal();
    this.querySelectorAll('[data-compare-at-price]').forEach(element => {
      element.textContent = MP.Utilities.formatMoney(compareAtTotal, window.theme?.moneyFormat);
    });
  }

  /**
   * Update the price display based on compare-at total and discount percentage
   */
  updatePriceDisplay() {
    const compareAtTotal = this.getCompareAtTotal();
    const discountPercent = this.getDiscountPercent();
    const discountedTotal = Math.round(compareAtTotal * (1 - discountPercent / 100));
    this.querySelectorAll('[data-price]').forEach(element => {
      element.textContent = MP.Utilities.formatMoney(discountedTotal, window.theme?.moneyFormat);
    });

    this.updateSavingsDisplay(discountedTotal, compareAtTotal);
  }

  /**
   * Update the savings display based on price and compare-at total
   * @param {number} priceTotal
   * @param {number} compareAtTotal
   */
  updateSavingsDisplay(priceTotal, compareAtTotal) {
    const savingsTotal = Math.max(compareAtTotal - priceTotal, 0);
    this.querySelectorAll('[data-price-saving]').forEach(element => {
      element.textContent = MP.Utilities.formatMoney(savingsTotal, window.theme?.moneyFormat);
    });
  }

  /**
   * Get the discount percent from the element attribute
   * @returns {number}
   */
  getDiscountPercent() {
    const rawValue = this.getAttribute('minimum-reward-value');
    const parsedValue = rawValue ? parseFloat(rawValue) : 0;
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }

  /**
   * Determine if a choice should be ignored for price calculations
   * @param {HTMLElement} choiceElement
   * @returns {boolean}
   */
  isIgnorePriceEnabled(choiceElement) {
    return choiceElement?.dataset?.ignorePriceEnabled === "true";
  }

  /**
   * Update the add-to-cart button text based on resolved availability
   */
  updateAvailabilityDisplay() {
    const button = this.querySelector('button[data-psp-add-to-cart]');
    const availableText = button?.querySelector('[data-psp-available-text]');
    const soldOutText = button?.querySelector('[data-psp-sold-out-text]');
    if (!button || !availableText || !soldOutText) return;

    const availabilityStates = Object.values(this.state)
      .map(choiceState => choiceState?.available)
      .filter(value => value !== null);

    if (!availabilityStates.length) return;

    const allAvailable = availabilityStates.every(value => value === true);

    if (allAvailable) {
      availableText.classList.remove('d-none');
      soldOutText.classList.add('d-none');
      button.removeAttribute('disabled');
    } else {
      availableText.classList.add('d-none');
      soldOutText.classList.remove('d-none');
      button.setAttribute('disabled', 'disabled');
    }
  }

  /**
   * Update estimated delivery display based on shipping_from metafields
   */
  updateEstimatedDeliveryDisplay() {
    const shippingFromValue = Object.values(this.state)
      .map(choiceState => choiceState?.shippingFrom)
      .find(value => value);

    const overrideContainer = this.querySelector('[data-estimated-delivery-override]');
    const standardContainer = this.querySelector('[data-gh-tpo-estimated-delivery]');
    if (!overrideContainer || !standardContainer) return;
    let shipsFromText = this.querySelector('[data-ships-from-date]');

    if (shippingFromValue) {
      const formattedDate = this.formatShippingFromDate(shippingFromValue);
      if (!shipsFromText) {
        overrideContainer.innerHTML = `<p data-ships-from-date>Ships from <b>${formattedDate}</b></p>`;
        shipsFromText = overrideContainer.querySelector('[data-ships-from-date]');
      } else {
        shipsFromText.innerHTML = `Ships from <b>${formattedDate}</b>`;
      }
      overrideContainer.classList.remove('d-none');
      standardContainer.classList.add('d-none');
      return;
    }

    standardContainer.classList.remove('d-none');
    overrideContainer.classList.add('d-none');
  }

  /**
   * Extract variant image data from the API response
   * Assumes featured_image is always an object with a src property
   * @param {Object} variantData
   * @returns {{src: string} | null}
   */
  getVariantImageData(variantData) {
    const src = variantData?.featured_image?.src;
    if (!src) {
      return null;
    }

    return { src: this.getResizedImageSrc(src, 120, 120) };
  }

  /**
   * Update the nested choice image for a given choice index
   * @param {string} choiceIndex
   */
  updateChoiceImage(choiceIndex) {
    const choiceElement = this.getChoiceElement(choiceIndex);
    const imageContainer = choiceElement?.querySelector('[data-psc-image]');
    const img = imageContainer?.querySelector('img');
    if (!img) return;
    const imageData = this.state[choiceIndex]?.image;
    if (!imageData?.src) return;

    const applyImageChanges = () => {
      img.setAttribute('src', imageData.src);
      img.removeAttribute('srcset');
    };

    try { document.startViewTransition(() => applyImageChanges()); } catch (e) { applyImageChanges(); }
  }

  /**
   * Insert a size suffix before the file extension
   * @param {string} src
   * @param {number} width
   * @param {number} height
   * @returns {string}
   */
  getResizedImageSrc(src, width, height) {
    const [path, query] = src.split("?");
    const lastDotIndex = path.lastIndexOf(".");
    if (lastDotIndex === -1) {
      return src;
    }

    const sizeSuffix = `_${width}x${height}`;
    const resizedPath = `${path.slice(0, lastDotIndex)}${sizeSuffix}${path.slice(lastDotIndex)}`;
    return query ? `${resizedPath}?${query}` : resizedPath;
  }

  /**
   * Build a resolved variant object from API data
   * @param {Object} variant
   * @param {Object} data
   * @returns {{id: number | null, price: number | null, compareAtPrice: number | null, available: boolean | null, image: { src: string } | null, shippingFrom: string | null}}
   */
  buildResolvedVariant(variant, data) {
    return {
      id: parseInt(variant.id),
      price: variant.price != null ? parseInt(variant.price) : null,
      compareAtPrice: variant.compare_at_price != null ? parseInt(variant.compare_at_price) : null,
      available: typeof variant.available === "boolean" ? variant.available : null,
      image: this.getVariantImageData(variant),
      shippingFrom: data?.variant_metafields?.m_p?.shipping_from ?? null
    };
  }

  /**
   * Apply resolved variant data to choice state
   * @param {Object} choiceState
   * @param {Object} resolvedVariant
   */
  applyResolvedVariant(choiceState, resolvedVariant) {
    if (!choiceState) return;
    Object.assign(choiceState, this.emptyVariantCacheEntry(), resolvedVariant || {});
  }

  /**
   * Empty cache entry for unresolved variants
   * @returns {{id: null, price: null, compareAtPrice: null, available: null, image: null, shippingFrom: null}}
   */
  emptyVariantCacheEntry() {
    return {
      id: null,
      price: null,
      compareAtPrice: null,
      available: null,
      image: null,
      shippingFrom: null
    };
  }

  /**
   * Get all resolved variant IDs for cart addition
   * @returns {Array<{id: number, quantity: number, properties?: Object}>}
   */
  getResolvedVariants() {
    const variants = [];
    
    Object.keys(this.state).forEach(choiceIndex => {
      const choiceState = this.state[choiceIndex];
      if (choiceState.id) {
        variants.push({
          id: choiceState.id,
          quantity: 1, // Default quantity, could be made configurable
          properties: {
            _nested_choice_index: choiceIndex,
            _is_nested_choice: true
          }
        });
      }
    });
    
    return variants;
  }

  /**
   * Get parent product ID from the product form
   * @returns {number|null}
   */
  getParentProductId() {
    const productForm = this.closest('form[data-type="add-to-cart-form"]');
    const hiddenInput = productForm?.querySelector('input[name="id"]');
    return hiddenInput?.value ? parseInt(hiddenInput.value) : null;
  }

  /**
   * Handle add to cart button click
   * @param {Event} e 
   */
  handleAddToCartClick(e) {
    e.preventDefault();
    this.addToCart();
  }

  /**
   * Add resolved variants to cart with parent_id property
   */
  async addToCart() {
    this.setAddToCartLoading(true);
    const parentProductId = this.getParentProductId();
    if (!parentProductId) {
      console.error(this.logPrefix, "Could not find parent product ID");
      this.setAddToCartLoading(false);
      return;
    }

    const resolvedVariants = this.getResolvedVariants();
    if (resolvedVariants.length === 0) {
      console.warn(this.logPrefix, "No resolved variants to add to cart");
      this.setAddToCartLoading(false);
      return;
    }

    const groupId = this.createGroupId();

    // Add parent_id property to each variant
    const variantsWithParent = resolvedVariants.map(variant => ({
      ...variant,
      parent_id: parentProductId,
      can_remove: false,
      instructions: {
        can_remove: false,
        canUpdateQuantity: false
      },
      properties: {
        ...variant.properties,
        _group_id: groupId
      }
    }));

    // Add the parent product to the cart as well
    variantsWithParent.push({
      id: parentProductId,
      quantity: 1,
      properties: {
        _is_parent_product: true,
        _group_id: groupId
      }
    });

    console.log(this.logPrefix, 'Adding variants to cart', variantsWithParent);

    // Add to cart
    try {
      await MP.Extensions.Cart.add(variantsWithParent);
    } catch (error) {
      console.error(this.logPrefix, "Failed to add variants to cart:", error);
    } finally {
      this.setAddToCartLoading(false);
    }
  }

  /**
   * Create a random 6-character group ID
   * @returns {string}
   */
  createGroupId() {
    return Math.random().toString(36).slice(2, 8).toUpperCase();
  }

  /**
   * Toggle add-to-cart loading UI with view transitions
   * @param {boolean} isLoading
   */
  setAddToCartLoading(isLoading) {
    const button = this.querySelector('button[data-psp-add-to-cart]');
    const spinner = button?.querySelector('[data-psp-add-to-cart-text] + div');
    if (!button || !spinner) return;

    const applyChanges = () => {
      if (isLoading) {
        button.classList.add('loading');
        spinner.classList.remove('d-none');
      } else {
        button.classList.remove('loading');
        spinner.classList.add('d-none');
      }
    };

    try { document.startViewTransition(() => applyChanges()); } catch (e) { applyChanges(); }
  }

  /**
   * Reset resolved variant fields for a choice state
   * @param {Object} choiceState
   */
  resetResolvedVariantState(choiceState) {
    if (!choiceState) return;
    Object.assign(choiceState, this.emptyVariantCacheEntry());
  }

  /**
   * Format a shipping_from date as "Wed, 21st"
   * @param {string} dateString
   * @returns {string}
   */
  formatShippingFromDate(dateString) {
    const date = new Date(`${dateString}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) return dateString;

    const locale = window.theme?.locale || "en-US";
    const weekday = date.toLocaleDateString(locale, { weekday: "short" });
    const day = date.getUTCDate();
    return `${weekday}, ${day}${this.getOrdinalSuffix(day)}`;
  }

  /**
   * Get ordinal suffix for a day number
   * @param {number} day
   * @returns {string}
   */
  getOrdinalSuffix(day) {
    if (day % 100 >= 11 && day % 100 <= 13) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  }

  /**
   * Sum compare-at prices across resolved variants
   * @returns {number}
   */
  getCompareAtTotal() {
    return Object.values(this.state).reduce((sum, choiceState) => {
      if (choiceState?.ignorePriceEnabled) return sum;
      const compareAtPrice = choiceState?.compareAtPrice;
      const fallbackPrice = choiceState?.price;
      const priceToUse = compareAtPrice ?? fallbackPrice;
      return priceToUse ? sum + priceToUse : sum;
    }, 0);
  }

  /**
   * Get the current state (useful for debugging or external access)
   * @returns {Object}
   */
  getState() {
    return { ...this.state };
  }
}

export default ProductSetPicker;
